import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { MdArrowBack } from "react-icons/md";
import PostModal from "components/community/PostModal";
import CommentModal from "components/community/CommentModal";
import DeleteConfirmationModal from "components/modals/DeleteConfirmationModal";
import LikersModal from "components/community/LikersModal";
import PostCard from "components/community/PostCard";
import CommentList from "components/community/CommentList";
import { usePost, usePostOperations } from "hooks/usePosts";
import { useComments, useCommentOperations } from "hooks/useComments";
import { useLikers } from "hooks/useLikers";
import { useAuth } from "contexts/AuthContext";
import {
  UpdatePostInput,
  CreateCommentInput,
  UpdateCommentInput,
  Comment,
  Post,
  PostAttachment,
} from "types/community";
import { CommunityService } from "services/communityService";
import ImageLightbox, { LightboxSlide } from "components/common/ImageLightbox";
import LoadingSpinner from "components/common/LoadingSpinner";
import { useImageLightbox } from "hooks/useImageLightbox";

const PostDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const initialPost = (location.state as { post?: Post })?.post;
  const { user } = useAuth();

  const postId = id && !isNaN(parseInt(id)) ? parseInt(id) : null;
  const {
    post,
    loading: postLoading,
    error: postError,
    refresh: refreshPost,
    updatePostData,
  } = usePost(postId);
  const {
    comments = [],
    loading: commentsLoading,
    error: commentsError,
    page: commentsPage,
    limit: commentsLimit,
    total: commentsTotal,
    totalPages: commentsTotalPages,
    changePage: changeCommentsPage,
    refresh: refreshComments,
    updateCommentInState,
  } = useComments(postId);

  const {
    loading: postOperationLoading,
    error: postOperationError,
    updatePost,
    deletePost,
    likePost,
    unlikePost,
    pinPost,
    unpinPost,
    clearError: clearPostError,
  } = usePostOperations();
  const {
    loading: commentOperationLoading,
    error: commentOperationError,
    createComment,
    updateComment,
    deleteComment,
    likeComment,
    unlikeComment,
    clearError: clearCommentError,
  } = useCommentOperations();

  const [isEditPostModalOpen, setIsEditPostModalOpen] = useState(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [editingComment, setEditingComment] = useState<Comment | null>(null);
  const [showDeletePostConfirm, setShowDeletePostConfirm] = useState(false);
  const [showDeleteCommentConfirm, setShowDeleteCommentConfirm] =
    useState<Comment | null>(null);
  const [likingPost, setLikingPost] = useState(false);
  const [likingComments, setLikingComments] = useState<Record<number, boolean>>(
    {}
  );
  const [pinningPost, setPinningPost] = useState(false);
  const { openAt, lightboxProps } = useImageLightbox();
  const [postLikeError, setPostLikeError] = useState<string | null>(null);
  const [commentLikeErrors, setCommentLikeErrors] = useState<
    Record<number, string>
  >({});
  const likersModal = useLikers();

  React.useEffect(() => {
    if (
      post &&
      !post.author.profile_image &&
      initialPost?.author.profile_image
    ) {
      updatePostData({
        ...post,
        author: {
          ...post.author,
          profile_image: initialPost.author.profile_image,
        },
      });
    }
  }, [post, initialPost, updatePostData]);

  const handleNavigateToUser = (userId: number) => {
    if (!userId) return;
    navigate(`/admin/users/${userId}`);
  };

  // Edit and delete share one rule: the author or an admin.
  const canManagePost = () => {
    return (
      post && user && (user.id === post.author.id || user.role === "admin")
    );
  };

  const canManageComment = (comment: Comment) => {
    return user && (user.id === comment.author.id || user.role === "admin");
  };

  const handleLikePost = async () => {
    if (!post || likingPost) return;

    setPostLikeError(null);
    setLikingPost(true);
    try {
      const result = post.is_liked_by_auth_user
        ? await unlikePost(post.id)
        : await likePost(post.id);

      if (result.success) {
        updatePostData({
          ...post,
          is_liked_by_auth_user: !post.is_liked_by_auth_user,
          likes_count: post.likes_count + (post.is_liked_by_auth_user ? -1 : 1),
        });
      } else {
        const fallbackMessage = post.is_liked_by_auth_user
          ? "Failed to unlike post. Please try again."
          : "Failed to like post. Please try again.";
        setPostLikeError(result.error || postOperationError || fallbackMessage);
      }
    } finally {
      setLikingPost(false);
    }
  };

  const handleOpenLikersModal = async () => {
    if (!post) return;
    await likersModal.open("Post Likes", () =>
      CommunityService.getPostLikers(post.id)
    );
  };

  const handleOpenCommentLikersModal = async (comment: Comment) => {
    await likersModal.open("Comment Likes", () =>
      CommunityService.getCommentLikers(comment.id)
    );
  };

  const handleToggleCommentLike = async (comment: Comment) => {
    if (likingComments[comment.id]) return;
    setCommentLikeErrors((prev) => {
      const { [comment.id]: _removed, ...rest } = prev;
      return rest;
    });

    const wasLiked = Boolean(comment.is_liked_by_auth_user);
    const optimisticLike = !wasLiked;
    const delta = optimisticLike ? 1 : -1;

    setLikingComments((prev) => ({ ...prev, [comment.id]: true }));

    updateCommentInState(comment.id, (currentComment) => ({
      ...currentComment,
      is_liked_by_auth_user: optimisticLike,
      likes_count: Math.max(0, (currentComment.likes_count || 0) + delta),
    }));

    const result = optimisticLike
      ? await likeComment(comment.id)
      : await unlikeComment(comment.id);

    if (result.success) {
      setCommentLikeErrors((prev) => {
        const { [comment.id]: _removed, ...rest } = prev;
        return rest;
      });
    } else {
      updateCommentInState(comment.id, () => comment);
      const fallbackMessage = optimisticLike
        ? "Failed to like comment. Please try again."
        : "Failed to unlike comment. Please try again.";
      setCommentLikeErrors((prev) => ({
        ...prev,
        [comment.id]: result.error || commentOperationError || fallbackMessage,
      }));
    }

    setLikingComments((prev) => {
      const { [comment.id]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handlePinPost = async () => {
    if (!post || pinningPost) return;

    setPinningPost(true);
    try {
      if (post.is_pinned) {
        const success = await unpinPost(post.id);
        if (success) {
          updatePostData({
            ...post,
            is_pinned: false,
          });
        }
      } else {
        const success = await pinPost(post.id);
        if (success) {
          updatePostData({
            ...post,
            is_pinned: true,
          });
        }
      }
    } finally {
      setPinningPost(false);
    }
  };

  const handleEditPost = () => {
    setIsEditPostModalOpen(true);
  };

  const handleDeletePost = () => {
    setShowDeletePostConfirm(true);
  };

  const handleUpdatePost = async (data: UpdatePostInput) => {
    if (!post) return;

    const updatedPost = await updatePost(post.id, data);
    if (updatedPost) {
      updatePostData(updatedPost);
      handleModalClose();
    }
  };

  const confirmDeletePost = async () => {
    if (!post) return;

    const success = await deletePost(post.id);
    if (success) {
      handleBack();
    }
    setShowDeletePostConfirm(false);
  };

  const handleCreateComment = () => {
    setEditingComment(null);
    setIsCommentModalOpen(true);
  };

  const handleEditComment = (comment: Comment) => {
    setEditingComment(comment);
    setIsCommentModalOpen(true);
  };

  const handleDeleteComment = (comment: Comment) => {
    setShowDeleteCommentConfirm(comment);
  };

  const handleCommentSubmit = async (
    data: CreateCommentInput | UpdateCommentInput
  ) => {
    if (!postId) return;

    if (editingComment) {
      const updatedComment = await updateComment(
        postId,
        editingComment.id,
        data as UpdateCommentInput
      );
      if (updatedComment) {
        refreshComments();
        handleModalClose();
      }
    } else {
      const newComment = await createComment(
        postId,
        data as CreateCommentInput
      );
      if (newComment) {
        refreshComments();
        // Update post's comments count
        if (post) {
          updatePostData({
            ...post,
            comments_count: post.comments_count + 1,
          });
        }
        handleModalClose();
      }
    }
  };

  const confirmDeleteComment = async () => {
    if (!showDeleteCommentConfirm || !postId) return;

    const success = await deleteComment(postId, showDeleteCommentConfirm.id);
    if (success) {
      refreshComments();
      // Update post's comments count
      if (post) {
        updatePostData({
          ...post,
          comments_count: Math.max(0, post.comments_count - 1),
        });
      }
    }
    setShowDeleteCommentConfirm(null);
  };

  const handleBack = () => {
    const returnPath =
      "/admin/community" + ((location.state as any)?.from || "");
    navigate(returnPath);
  };

  const handleModalClose = () => {
    setIsEditPostModalOpen(false);
    setIsCommentModalOpen(false);
    setEditingComment(null);
    setPostLikeError(null);
    setCommentLikeErrors({});
    clearPostError();
    clearCommentError();
  };

  // Post attachments are named, not dated - captions use the filename rather
  // than buildFileSlides' upload date.
  const buildAttachmentSlides = (
    attachments: PostAttachment[]
  ): LightboxSlide[] =>
    attachments.map((attachment) => ({
      src: attachment.full_path,
      alt: attachment.original_name || "Attachment",
      description: attachment.original_name || attachment.storage_name,
    }));

  const handleImageClick = (attachments: PostAttachment[], index: number) => {
    openAt(buildAttachmentSlides(attachments), index);
  };

  if (postLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner bare size="lg" />
      </div>
    );
  }

  if (postError || !post) {
    return (
      <div className="py-12 text-center">
        <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">
          {postError || "Post not found"}
        </h3>
        <button
          onClick={handleBack}
          className="inline-flex items-center text-biafit-primary hover:text-biafit-secondary"
        >
          <MdArrowBack className="h-6 w-6 md:mr-2 md:h-4 md:w-4" />
          <span className="hidden md:inline">Return to Community</span>
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3 grid h-full grid-cols-1 gap-5">
      <div className="col-span-1 h-fit w-full">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBack}
              className="flex items-center space-x-2 text-gray-600 transition-colors hover:text-biafit-primary dark:text-gray-400"
            >
              <MdArrowBack className="h-6 w-6 md:h-5 md:w-5" />
              <span className="hidden md:inline">Back to Community</span>
            </button>
          </div>
        </div>

        <PostCard
          post={post}
          fallbackProfileImage={initialPost?.author.profile_image?.full_path}
          canPin={user?.role === "admin"}
          canManage={!!canManagePost()}
          pinning={pinningPost}
          liking={likingPost}
          likeError={postLikeError}
          onPin={handlePinPost}
          onEdit={handleEditPost}
          onDelete={handleDeletePost}
          onLike={handleLikePost}
          onOpenLikers={handleOpenLikersModal}
          onImageClick={handleImageClick}
        />

        <CommentList
          comments={comments}
          loading={commentsLoading}
          error={commentsError}
          page={commentsPage}
          limit={commentsLimit}
          total={commentsTotal}
          totalPages={commentsTotalPages}
          onPageChange={changeCommentsPage}
          canManage={(comment) => !!canManageComment(comment)}
          likingComments={likingComments}
          likeErrors={commentLikeErrors}
          onAdd={handleCreateComment}
          onEdit={handleEditComment}
          onDelete={handleDeleteComment}
          onToggleLike={handleToggleCommentLike}
          onOpenLikers={handleOpenCommentLikersModal}
          onImageClick={handleImageClick}
        />
      </div>

      {/* Modals */}
      <PostModal
        isOpen={isEditPostModalOpen}
        onClose={handleModalClose}
        onSubmit={handleUpdatePost}
        post={post}
        loading={postOperationLoading}
        error={postOperationError}
      />

      <CommentModal
        isOpen={isCommentModalOpen}
        onClose={handleModalClose}
        onSubmit={handleCommentSubmit}
        comment={editingComment}
        loading={commentOperationLoading}
        error={commentOperationError}
      />

      <LikersModal
        isOpen={likersModal.isOpen}
        onClose={likersModal.close}
        likers={likersModal.likers}
        loading={likersModal.loading}
        error={likersModal.error}
        onRetry={likersModal.retry}
        title={likersModal.title}
        onUserClick={(liker) => handleNavigateToUser(liker.id)}
      />

      {/* Delete Post's Confirmation */}
      <DeleteConfirmationModal
        isOpen={showDeletePostConfirm}
        onClose={() => setShowDeletePostConfirm(false)}
        onConfirm={confirmDeletePost}
        title="Delete Post"
        message="Are you sure you want to delete this post? This action cannot be undone and will also delete all comments."
        itemName={post?.content.substring(0, 50) || ""}
        loading={postOperationLoading}
        error={postOperationError}
      />

      {/* Delete Comment Confirmation */}
      <DeleteConfirmationModal
        isOpen={!!showDeleteCommentConfirm}
        onClose={() => setShowDeleteCommentConfirm(null)}
        onConfirm={confirmDeleteComment}
        title="Delete Comment"
        message="Are you sure you want to delete this comment? This action cannot be undone."
        itemName={showDeleteCommentConfirm?.content.substring(0, 50) || ""}
        loading={commentOperationLoading}
        error={commentOperationError}
      />

      {/* Lightbox Modal for Attachments */}
      <ImageLightbox {...lightboxProps} />
    </div>
  );
};

export default PostDetail;
